import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import LanguageToggle from "@/components/LanguageToggle";
import ObfuscatedContact from "@/components/ObfuscatedContact";
const Footer = () => {
  const { t } = useTranslation();
  const { localizedPath } = useLocalizedPath();

  const quickLinks = [
    { label: t("nav.about"), href: localizedPath("/about") },
    { label: t("nav.talent"), href: localizedPath("/talent") },
    { label: t("nav.services"), href: localizedPath("/services") },
    { label: t("nav.join"), href: localizedPath("/for-talent") },
    { label: t("nav.contact"), href: localizedPath("/contact") },
  ];

  return (
    <footer id="contact" className="bg-background border-t border-border">
      <div className="container-wide section-padding px-5 sm:px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-4 md:gap-8">
          {/* Logo & Description */}
          <div className="sm:col-span-2">
          <Link to={localizedPath("/")} className="flex items-center mb-5 sm:mb-6">
              <img 
                src="https://vpibveaztrzmapeuvvxv.supabase.co/storage/v1/object/public/media/firststage-logo-SM.png" 
                alt="First Stage Talent Agency" 
                className="h-20 sm:h-24 w-auto"
              />
            </Link>
            <p className="text-muted-foreground max-w-sm mb-5 sm:mb-6 whitespace-pre-line text-[0.9375rem] sm:text-base">
              {t("footer.description")}
            </p>
            <LanguageToggle variant="footer" />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-wide mb-4 sm:mb-6 text-primary">{t("footer.quickLinks")}</h4>
            <ul className="space-y-3">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <Link 
                    to={item.href} 
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm py-1 inline-block min-h-[44px] flex items-center"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-wide mb-4 sm:mb-6 text-primary">{t("footer.contact")}</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail size={16} aria-hidden="true" className="flex-shrink-0 text-primary" />
                <ObfuscatedContact 
                  type="email" 
                  className="hover:text-foreground transition-colors inline-link py-1"
                />
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone size={16} aria-hidden="true" className="flex-shrink-0 text-primary" />
                <ObfuscatedContact 
                  type="phone" 
                  className="hover:text-foreground transition-colors inline-link py-1"
                />
              </li>
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin size={16} className="mt-0.5 text-primary flex-shrink-0" aria-hidden="true" />
                <span>Ottawa-Gatineau<br />Ontario / Québec, Canada</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-border flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            {t("footer.copyright", { year: new Date().getFullYear() })}{" "}
            {t("footer.craftedBy")}{" "}
            <a 
              href="https://airow.ca" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors underline"
            >
              Airow
            </a>
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground justify-center sm:justify-end">
            <Link to={localizedPath("/privacy")} className="hover:text-foreground transition-colors inline-link py-2 min-h-[44px] flex items-center">{t("footer.privacy")}</Link>
            <Link to={localizedPath("/terms")} className="hover:text-foreground transition-colors inline-link py-2 min-h-[44px] flex items-center">{t("footer.terms")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
